import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MdOutlineFileDownload } from "react-icons/md";
import Badge from "../../common/Badge/Badge";
import Modal from "../../common/Modal/Modal";
import {
    BADGE_VALORACION_VISITA,
    SECCIONES_INFORME_VISITA,
    descargarInformeVisitaWord,
    formatFechaVisita,
} from "./visitaInforme";
import "./VisitaDetalleModal.scss";

// Vista de solo lectura del informe de una visita. La usan la pestaña
// "Visitas" de la ficha del gestor y la pagina de Informes de visitas.
// gestorNombre solo hace falta cuando la visita no trae resuelto "proveedor".
export default function VisitaDetalleModal({
    isOpen,
    onClose,
    visita,
    gestorNombre,
}) {
    const { t } = useTranslation();
    const [exportando, setExportando] = useState(false);
    const [errorExport, setErrorExport] = useState(null);

    const badge = visita
        ? BADGE_VALORACION_VISITA[visita.codValoracion]
        : null;

    // El componente no se desmonta al cerrar el modal: sin esto el error de una
    // exportacion fallida seguiria ahi al abrir el siguiente informe.
    useEffect(() => {
        setErrorExport(null);
    }, [isOpen, visita]);

    const handleExportarWord = async () => {
        if (exportando || !visita) return;

        setExportando(true);
        setErrorExport(null);

        try {
            await descargarInformeVisitaWord(visita);
        } catch (err) {
            console.error("Error exportando el informe de visita a Word:", err);
            setErrorExport(
                t("visitDetail.wordError")
            );
        } finally {
            setExportando(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            position="center"
            className="modal--visita-detalle"
            title={visita?.titulo || t("visitDetail.visitReport")}
            actions={[
                <button
                    key="exportar"
                    className="visita-detalle__export-btn"
                    onClick={handleExportarWord}
                    disabled={exportando || !visita}
                >
                    <MdOutlineFileDownload size={16} />
                    {exportando ? t("visitDetail.generating") : t("visitDetail.exportWord")}
                </button>,
                <button
                    key="cerrar"
                    className="visita-detalle__close-btn"
                    onClick={onClose}
                >
                    {t("visitDetail.close")}
                </button>,
            ]}
        >
            {visita && (
                <div className="visita-detalle">
                    <div className="visita-detalle__cabecera">
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.dateColon")}</label>
                            <span>{formatFechaVisita(visita.fechaVisita)}</span>
                        </div>
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.managerColon")}</label>
                            <span>{visita.proveedor || gestorNombre || "-"}</span>
                        </div>
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.locationColon")}</label>
                            <span>{visita.ubicacion || "-"}</span>
                        </div>
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.facilityTypeColon")}</label>
                            <span>{visita.tipoInstalacion || "-"}</span>
                        </div>
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.technicianColon")}</label>
                            <span>{visita.tecnico || "-"}</span>
                        </div>
                        <div className="visita-detalle__field">
                            <label>{t("visitDetail.ratingColon")}</label>
                            <span className="visita-detalle__field-badge">
                                {badge ? (
                                    <Badge variant={badge.type} Icon={badge.Icon}>
                                        {badge.label}
                                    </Badge>
                                ) : (
                                    "-"
                                )}
                            </span>
                        </div>
                    </div>

                    {SECCIONES_INFORME_VISITA.filter(
                        (seccion) => visita[seccion.key]
                    ).map((seccion) => (
                        <div
                            key={seccion.key}
                            className="visita-detalle__seccion"
                        >
                            <h3>{seccion.label}</h3>
                            <p>{visita[seccion.key]}</p>
                        </div>
                    ))}

                    {visita.documento && visita.ficheroBase64 && (
                        <div className="visita-detalle__adjunto">
                            <a
                                href={`data:application/pdf;base64,${visita.ficheroBase64}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download={visita.documento}
                            >
                                {visita.documento}
                            </a>
                        </div>
                    )}

                    {errorExport && (
                        <div className="visita-detalle__error">
                            {errorExport}
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}
